import VincularPedido from "components/VincularPedido";
import {
  Box,
  Button,
  Grid,
  Card,
  TextField,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import Service from "services/receivables";
import PermissionService from "services/permission";
import BatchOfSettlementsService from "services/batchOfSettlements";
import AccountSubgroupService from "services/sub-group-accounts";
import AccountGroupsService from "services/account-groups";
import PaymentMethodService from "services/payment-methods";
import CustomerService from "services/customers";
import AccountService from "services/accounts";
import CurrencyInput from "react-currency-input-field";
import { format } from "utils/formatDate";
import {
  yupSchema,
  initialValues,
  BILLING_STATUS,
  findBillingStatus,
} from "./yupSchema";

export default function EditarContaAReceber({
  setShowEditState,
  currentIdAction,
  fetchData,
  fecharOnSave = true,
  dataPedido = null,
}) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [alertKey, setAlertKey] = useState(0);
  const [dataList, setDataList] = useState({
    accountSubgroup: [],
    accountGroups: [],
    paymentMethods: [],
    customers: [],
    accounts: [],
  });
  const [pedidoVinculadoNum, setPedidoVinculadoNum] = useState("");
  const [isStatusPago, setIsStatusPago] = useState(false);
  const [batches, setBatches] = useState([]);
  // Valor reduzido: oferece gerar outra conta com a diferença (vencimento sugerido = entrega do pedido)
  const [valorOriginal, setValorOriginal] = useState(null);
  const [dataEntregaPedido, setDataEntregaPedido] = useState(null);
  const [diferenca, setDiferenca] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: accountGroups } = await AccountGroupsService.list();
        const { data: accountSubgroup } = await AccountSubgroupService.list();
        const { data: paymentMethods } = await PaymentMethodService.list();
        const { data: customers } = await CustomerService.list();
        const { data: accounts } = await AccountService.list();

        setDataList({
          accountSubgroup,
          accountGroups,
          paymentMethods,
          customers,
          accounts,
        });
      } catch (error) {
        console.log(error);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const parseValor = (valor) =>
    typeof valor === "number"
      ? valor
      : Number(
          String(valor ?? "")
            .replace("R$", "")
            .trim()
            .replace(",", ".")
        ) || 0;

  const finalizarSalvar = () => {
    if (fecharOnSave) {
      fetchData?.();
      setShowEditState?.(false);
    } else {
      setSuccess(true);
    }
  };

  const gerarContaDiferenca = async () => {
    setLoading(true);
    try {
      const v = formik.values;
      await Service.create({
        descricao: `Diferença - ${v.descricao || ""}`.trim(),
        customer: v.customer,
        paymentMethod: v.paymentMethod,
        account: v.account,
        accountGroup: v.accountGroup,
        accountSubGroup: v.accountSubGroup,
        partidaContabil: v.partidaContabil,
        sale: v.sale || null,
        prazoPagamento: v.prazoPagamento,
        observacoes: v.observacoes,
        dataPedido: v.dataPedido || null,
        dataVencimento: diferenca.dataVencimento || null,
        valor: diferenca.valor.toFixed(2),
        status: BILLING_STATUS.ABERTO,
      });
      setDiferenca(null);
      finalizarSalvar();
    } catch (error) {
      console.log(error);
      setSuccess(false);
      setAlertKey((k) => k + 1);
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: yupSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        // Validação adicional antes de enviar
        if (values.status === BILLING_STATUS.PAGO) {
          if (!values.dataPagamento) {
            setSuccess(false);
            setAlertKey(alertKey + 1);
            setLoading(false);
            return;
          }

          if (!values.account) {
            setSuccess(false);
            setAlertKey(alertKey + 1);
            setLoading(false);
            return;
          }

          const valorNumerico = parseFloat(
            (values.valor || "")
              .toString()
              .replace(/[^\d,.-]/g, "")
              .replace(",", ".")
          );
          if (!valorNumerico || valorNumerico <= 0) {
            setSuccess(false);
            setAlertKey(alertKey + 1);
            setLoading(false);
            return;
          }
        }

        await Service.update(currentIdAction, values);

        const reducao =
          valorOriginal !== null ? valorOriginal - parseValor(values.valor) : 0;
        if (reducao > 0.01 && values.status !== BILLING_STATUS.CANCELADO) {
          setDiferenca({
            valor: reducao,
            dataVencimento: dataEntregaPedido
              ? format(new Date(dataEntregaPedido), "yyyy-MM-dd")
              : format(new Date(), "yyyy-MM-dd"),
          });
        } else {
          finalizarSalvar();
        }
      } catch (error) {
        console.log(error);
        setSuccess(false);
      }
      setAlertKey(alertKey + 1);
      setLoading(false);
    },
  });

  useEffect(() => {
    const fetchList = async () => {
      setLoading(true);
      const fields = await Service.get(currentIdAction);
      setPedidoVinculadoNum(fields.sale?.numPedido ?? "");
      setValorOriginal(parseValor(fields.valor));
      setDataEntregaPedido(fields.sale?.dataDescarga || null);
      const batch = await BatchOfSettlementsService.getByReceivable(
        currentIdAction
      );
      setBatches(batch);
      const valorPago = batch.reduce((batchAcc, currentBatch) => {
        return (
          batchAcc +
          currentBatch.receivables
            .filter((item) => item.receivable_id === currentIdAction)
            .reduce((acc, item) => acc + item.valor, 0)
        );
      }, 0);
      formik.setFieldValue("valorAberto", (valorPago - fields.valor) * -1);
      for (var key in formik.values) {
        if (key.includes("data"))
          formik.setFieldValue(
            key,
            fields[key] ? format(new Date(fields[key]), "yyyy-MM-dd") : ""
          );
        else if (
          key === "customer" ||
          key === "paymentMethod" ||
          key === "sale" ||
          key === "account" ||
          key === "accountGroup" ||
          key === "accountSubGroup"
        ) {
          if (fields[key] !== undefined && fields[key] !== null) {
            formik.setFieldValue(key, fields[key]._id || fields[key]);
          }
        } else formik.setFieldValue(key, fields[key]);
      }

      const permissions = await PermissionService.listMy();
      const permissionsFiltered = permissions.filter(
        (permission) => permission.area === "receivables"
      );

      const isAdmin = permissionsFiltered.shift()?.editaAdmin === "1";
      if (fields.status === BILLING_STATUS.PAGO && !isAdmin) {
        setIsStatusPago(true);
      }

      setLoading(false);
    };

    fetchList();
  }, [currentIdAction, dataList]);

  const changeCurrency = (value, name) => {
    formik.setFieldValue(name, value);
  };

  const addZero = (e) => {
    const { name, value } = e.target;
    if (value === "") {
      formik.setFieldValue(name, "0,00");
    } else {
      const removeRS = value.replace("R$", "").trim();
      const removeDots = removeRS.replace(/\./g, "");

      if (value.indexOf(",") === -1) {
        formik.setFieldValue(name, `${removeDots},00`);
      }
    }
  };

  const handleCustomerChange = (e) => {
    formik.handleChange(e);

    const customer = dataList.customers.find((cs) => cs._id == e.target.value);

    if (customer && customer.paymentMethod) {
      formik.setFieldValue("paymentMethod", customer.paymentMethod);
    }
  };

  useEffect(() => {
    const closeOnEscapePressed = (e) => {
      if (e.key === "Escape") {
        fetchData();
        setShowEditState(false);
        setSuccess(null);
      }
    };
    window.addEventListener("keydown", closeOnEscapePressed);
    return () => window.removeEventListener("keydown", closeOnEscapePressed);
  }, []);

  useEffect(() => {
    if (dataPedido && formik.values.dataVencimento) {
      const dataPedidoFormatada = new Date(dataPedido);
      const dataVencimento = new Date(formik.values.dataVencimento);

      if (dataVencimento < dataPedidoFormatada) {
        formik.setFieldValue("dataVencimento", null);
        alert(
          "A data do vencimento indicada é inferior a data do pedido, por favor, realize a correção."
        );
      }
    }
  }, [formik.values.dataVencimento]);

  const validateDataVencimento = () => {
    if (formik.values.dataPedido && formik.values.dataVencimento) {
      if (formik.values.dataPedido > formik.values.dataVencimento) {
        alert(
          "A data do vencimento indicada é inferior a data do pedido, por favor, realize a correção."
        );
        formik.setFieldValue("dataVencimento", null);
      }
    }
  };

  useEffect(() => {
    const fetchValorPago = async () => {
      const valorPago = batches.reduce((batchAcc, currentBatch) => {
        return (
          batchAcc +
          currentBatch.receivables
            .filter((item) => item.receivable_id === currentIdAction)
            .reduce((acc, item) => acc + item.valor, 0)
        );
      }, 0);

      const fields = await Service.get(currentIdAction);
      formik.setFieldValue("valorAberto", (valorPago - fields.valor) * -1);
    };
    fetchValorPago();
  }, [batches]);

  return (
    <>
      <MDBox
        pt={"30px"}
        sx={{
          position: "absolute",
          zIndex: 9999,
          width: "100vw",
          minHeight: "100vh",
          top: 0,
          right: 0,
        }}
      >
        <MDBox
          pt={"30px"}
          sx={{
            position: "fixed",
            zIndex: -1,
            width: "100vw",
            height: "110%",
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(5px)",
            top: 0,
            right: 0,
          }}
        />
        <Grid container spacing={6}>
          <Grid item xs={12} sm={12} md={8} mx={"auto"}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white" mt={1}>
                  Editar Conta a receber
                </MDTypography>
                <MDButton
                  variant="contained"
                  type="button"
                  onClick={() => {
                    fetchData();
                    setSuccess(null);
                    setShowEditState(false);
                  }}
                >
                  Voltar
                </MDButton>
              </MDBox>
              <MDBox pt={3} mx={2} py={3} px={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 2,
                    mb: 2,
                  }}
                  component="form"
                  onSubmit={formik.handleSubmit}
                >
                  <TextField
                    id="partidaContabil"
                    name="partidaContabil"
                    label="Partida Contábil"
                    type="number"
                    value={formik.values.partidaContabil}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.partidaContabil &&
                      Boolean(formik.errors.partidaContabil)
                    }
                    helperText={
                      formik.touched.partidaContabil &&
                      formik.errors.partidaContabil
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <VincularPedido
                    formik={formik}
                    tipo="venda"
                    numPedidoInicial={pedidoVinculadoNum}
                    disabled={isStatusPago}
                  />

                  <TextField
                    id="descricao"
                    name="descricao"
                    label="Descrição"
                    value={formik.values.descricao}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.descricao &&
                      Boolean(formik.errors.descricao)
                    }
                    helperText={
                      formik.touched.descricao && formik.errors.descricao
                    }
                    disabled={isStatusPago}
                  />

                  <TextField
                    id="customer"
                    name="customer"
                    label="Cliente"
                    value={formik.values.customer}
                    onChange={handleCustomerChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.customer && Boolean(formik.errors.customer)
                    }
                    helperText={
                      formik.touched.customer && formik.errors.customer
                    }
                    select
                    SelectProps={{
                      native: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isStatusPago}
                  >
                    <option value="" />
                    {dataList.customers
                      .filter((option) => option.ativo == true)
                      .map((option) => (
                        <option key={option._id} value={option._id}>
                          {option.razaoSocial || option.nomeFantasia}
                        </option>
                      ))}
                  </TextField>

                  <TextField
                    id="paymentMethod"
                    name="paymentMethod"
                    label="Método de Pagamento"
                    value={formik.values.paymentMethod}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.paymentMethod &&
                      Boolean(formik.errors.paymentMethod)
                    }
                    helperText={
                      formik.touched.paymentMethod &&
                      formik.errors.paymentMethod
                    }
                    select
                    SelectProps={{
                      native: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isStatusPago}
                  >
                    <option value="" />
                    {dataList.paymentMethods.map((option) => (
                      <option key={option._id} value={option._id}>
                        {option.descricao}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    id="dataPedido"
                    name="dataPedido"
                    label="Data do Pedido"
                    type="date"
                    value={formik.values.dataPedido}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.dataPedido &&
                      Boolean(formik.errors.dataPedido)
                    }
                    helperText={
                      formik.touched.dataPedido && formik.errors.dataPedido
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isStatusPago}
                  />

                  <TextField
                    id="dataVencimento"
                    name="dataVencimento"
                    label="Data de Vencimento"
                    type="date"
                    value={formik.values.dataVencimento}
                    onChange={formik.handleChange}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      validateDataVencimento();
                    }}
                    error={
                      formik.touched.dataVencimento &&
                      Boolean(formik.errors.dataVencimento)
                    }
                    helperText={
                      formik.touched.dataVencimento &&
                      formik.errors.dataVencimento
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isStatusPago}
                  />

                  <TextField
                    id="dataPagamento"
                    name="dataPagamento"
                    label="Data de Pagamento"
                    type="date"
                    value={formik.values.dataPagamento}
                    onChange={formik.handleChange}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      validateDataVencimento();
                    }}
                    error={
                      formik.touched.dataPagamento &&
                      Boolean(formik.errors.dataPagamento)
                    }
                    helperText={
                      formik.touched.dataPagamento &&
                      formik.errors.dataPagamento
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isStatusPago}
                    required={formik.values.status === BILLING_STATUS.PAGO}
                  />

                  <CurrencyInput
                    id="valor"
                    name="valor"
                    label="Valor"
                    value={formik.values.valor}
                    onBlur={formik.handleBlur}
                    disabled={loading || isStatusPago}
                    decimalsLimit={2}
                    onValueChange={changeCurrency}
                    onBlurCapture={addZero}
                    customInput={TextField}
                    error={formik.touched.valor && Boolean(formik.errors.valor)}
                    helperText={formik.touched.valor && formik.errors.valor}
                    sx={{
                      width: "100%",
                    }}
                    prefix="R$ "
                    intlConfig={{ locale: "pt-BR", currency: "BRL" }}
                  />

                  <CurrencyInput
                    id="valorAberto"
                    name="valorAberto"
                    label="Valor em Aberto"
                    value={formik.values.valorAberto}
                    onBlur={formik.handleBlur}
                    disabled={true}
                    decimalsLimit={2}
                    onValueChange={changeCurrency}
                    onBlurCapture={addZero}
                    customInput={TextField}
                    error={
                      formik.touched.valorAberto &&
                      Boolean(formik.errors.valorAberto)
                    }
                    helperText={
                      formik.touched.valorAberto && formik.errors.valorAberto
                    }
                    sx={{
                      width: "100%",
                    }}
                    prefix="R$ "
                    intlConfig={{ locale: "pt-BR", currency: "BRL" }}
                    InputProps={{
                      readOnly: true,
                      disabled: true,
                    }}
                  />

                  <TextField
                    id="status"
                    name="status"
                    label="Status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={formik.touched.status && formik.errors.status}
                    select
                    SelectProps={{
                      native: true,
                    }}
                    disabled={isStatusPago}
                  >
                    <option value=""></option>
                    {Object.values(BILLING_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {findBillingStatus(status)}
                      </option>
                    ))}
                  </TextField>

                  <TextField
                    id="account"
                    name="account"
                    label="Conta"
                    value={formik.values.account}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.account && Boolean(formik.errors.account)
                    }
                    helperText={formik.touched.account && formik.errors.account}
                    select
                    SelectProps={{
                      native: true,
                    }}
                    InputLabelProps={
                      formik.values.account?.length > 0
                        ? {
                            shrink: true,
                          }
                        : {}
                    }
                    disabled={isStatusPago}
                    required={formik.values.status === BILLING_STATUS.PAGO}
                  >
                    <option value="" />
                    {dataList.accounts.map((option) => (
                      <option key={option._id} value={option._id}>
                        {option.nome}
                      </option>
                    ))}
                  </TextField>

                  <TextField
                    id="accountGroup"
                    name="accountGroup"
                    label="Centro de Custo"
                    value={formik.values.accountGroup}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.accountGroup &&
                      Boolean(formik.errors.accountGroup)
                    }
                    helperText={
                      formik.touched.accountGroup && formik.errors.accountGroup
                    }
                    select
                    SelectProps={{
                      native: true,
                    }}
                    InputLabelProps={
                      formik.values.accountGroup?.length > 0
                        ? {
                            shrink: true,
                          }
                        : {}
                    }
                    disabled={isStatusPago}
                  >
                    <option value="" />
                    {dataList.accountGroups.map((option) => (
                      <option key={option._id} value={option._id}>
                        {option.codigo ? option.codigo + " - " : ""}{" "}
                        {option.descricao}
                      </option>
                    ))}
                  </TextField>

                  <TextField
                    id="accountSubGroup"
                    name="accountSubGroup"
                    label="Natureza de Lançamento"
                    value={formik.values.accountSubGroup}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.accountSubGroup &&
                      Boolean(formik.errors.accountSubGroup)
                    }
                    helperText={
                      formik.touched.accountSubGroup &&
                      formik.errors.accountSubGroup
                    }
                    select
                    SelectProps={{
                      native: true,
                    }}
                    InputLabelProps={
                      formik.values.accountSubGroup?.length > 0
                        ? {
                            shrink: true,
                          }
                        : {}
                    }
                    disabled={isStatusPago}
                  >
                    <option value="" />
                    {dataList.accountSubgroup.map((option) => (
                      <option key={option._id} value={option._id}>
                        {option.nome}
                      </option>
                    ))}
                  </TextField>

                  <TextField
                    id="observacoes"
                    name="observacoes"
                    label="Observação"
                    value={formik.values.observacoes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.observacoes &&
                      Boolean(formik.errors.observacoes)
                    }
                    helperText={
                      formik.touched.observacoes && formik.errors.observacoes
                    }
                    multiline
                    rows={4}
                    sx={{
                      gridColumn: "span 2",
                      justifySelf: "center",
                      width: "100%",
                    }}
                    disabled={isStatusPago}
                  />

                  {batches.length > 0 && (
                    <MDBox
                      sx={{
                        gridColumn: "span 2",
                        justifySelf: "center",
                        width: "100%",
                      }}
                    >
                      <MDButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        iconOnly
                        sx={{
                          width: "100%",
                          color: "white !important",
                          cursor: "pointer",
                        }}
                        readOnly
                      >
                        <Icon fontSize="small" sx={{ mr: 1 }}>
                          inbox
                        </Icon>
                        Lotes de baixa em que é mencionado
                      </MDButton>

                      {batches.map((batch) => (
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            marginTop: "10px",
                            backgroundColor: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#f5f5f5",
                                borderBottom: "2px solid #ddd",
                              }}
                            >
                              <th
                                style={{ padding: "12px", textAlign: "left" }}
                              >
                                Lote
                              </th>
                              <th
                                style={{ padding: "12px", textAlign: "left" }}
                              >
                                Descrição
                              </th>
                              <th
                                style={{ padding: "12px", textAlign: "left" }}
                              >
                                Conta de Origem
                              </th>
                              <th
                                style={{ padding: "12px", textAlign: "left" }}
                              >
                                Data do Recebimento
                              </th>
                              <th
                                style={{ padding: "12px", textAlign: "right" }}
                              >
                                Valor da Baixa
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ borderBottom: "1px solid #ddd" }}>
                              <td style={{ padding: "12px" }}>{batch.nome}</td>
                              <td style={{ padding: "12px" }}>
                                {batch.descricao}
                              </td>
                              <td style={{ padding: "12px" }}>
                                {batch.account.nome}
                              </td>
                              <td style={{ padding: "12px" }}>
                                {(batch.receivables.find(
                                  (item) =>
                                    item.receivable_id === currentIdAction
                                )?.dataBaixa ||
                                  batch.dataPagamento) &&
                                  format(
                                    new Date(
                                      batch.receivables.find(
                                        (item) =>
                                          item.receivable_id === currentIdAction
                                      )?.dataBaixa || batch.dataPagamento
                                    ),
                                    "dd/MM/yyyy"
                                  )}
                              </td>
                              <td
                                style={{ padding: "12px", textAlign: "right" }}
                              >
                                {new Intl.NumberFormat("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                }).format(
                                  batch.receivables.find(
                                    (item) =>
                                      item.receivable_id === currentIdAction
                                  )?.valor || 0
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      ))}
                    </MDBox>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      gridColumn: "span 2",
                      justifySelf: "center",
                      width: "100%",
                      color: "white !important",
                    }}
                  >
                    {loading ? "Carregando..." : "Salvar"}
                  </Button>
                </Box>

                {success !== null && (
                  <Box>
                    <MDAlert
                      color={success ? "success" : "error"}
                      mt={4}
                      dismissible
                      key={alertKey}
                    >
                      <MDTypography variant="body2" color="white">
                        {success
                          ? "Conta a receber atualizada com sucesso!"
                          : formik.values.status === BILLING_STATUS.PAGO &&
                            (!formik.values.dataPagamento ||
                              !formik.values.account ||
                              parseFloat(
                                formik.values.valor
                                  .replace(/[^\d,.-]/g, "")
                                  .replace(",", ".")
                              ) <= 0)
                          ? "Para salvar com status PAGO, preencha: Data de Pagamento, Conta Bancária e um Valor maior que zero."
                          : "Ocorreu um erro ao atualizar a conta a receber."}
                      </MDTypography>
                    </MDAlert>
                  </Box>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={Boolean(diferenca)}
        onClose={() => {
          setDiferenca(null);
          finalizarSalvar();
        }}
        sx={{ zIndex: 10000 }}
      >
        <DialogTitle>Gerar conta com a diferença?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" mb={2}>
            O valor da conta foi reduzido em{" "}
            <b>
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(diferenca?.valor || 0)}
            </b>
            . Deseja gerar uma nova conta a receber com essa diferença?
          </MDTypography>
          <TextField
            type="date"
            label="Vencimento da nova conta"
            value={diferenca?.dataVencimento || ""}
            onChange={(e) =>
              setDiferenca((d) => ({ ...d, dataVencimento: e.target.value }))
            }
            helperText={
              dataEntregaPedido
                ? "Sugestão: data de entrega do pedido"
                : "Pedido sem data de entrega: sugerida a data de hoje"
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDiferenca(null);
              finalizarSalvar();
            }}
          >
            Não gerar
          </Button>
          <Button
            variant="contained"
            sx={{ color: "white !important" }}
            disabled={loading}
            onClick={gerarContaDiferenca}
          >
            Gerar conta
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
